import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    AUDIENCE,
    CATEGORY_LABEL,
    CTA_EXTERNAL,
    LIFECYCLE,
    audienceSummary,
    whenText,
} from '../../helpers/trainerNotifications'
import TrainerNotificationService from '../../services/trainerNotificationService'
import { errorMessage } from '../../services/axios/errorMessage'
import { SURFACE, TEXT, cardSx, pageSx } from '../../theme/tokens'
import StripPreview from './StripPreview'
import {
    LifecycleChip,
    NotificationHeader,
    TagPill,
} from './notificationShared'

const cellSx = { borderColor: SURFACE.border, typography: 'caption' }

// How many listed trainer ids the audience shows.
const SHOWN_IDS = 20

/** One labelled value in the details grid. */
const Field = ({ label, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            variant="overline"
            component="div"
            sx={{ color: TEXT.muted, lineHeight: 1.8 }}
        >
            {label}
        </Typography>
        <Box
            sx={{
                typography: 'body2',
                color: TEXT.primary,
                wordBreak: 'break-word',
            }}
        >
            {children}
        </Box>
    </Box>
)
Field.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
}

/** A big number with its caption (viewed / acknowledged). */
const Stat = ({ label, value }) => (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minWidth: 160 }}>
        <Typography
            sx={{ typography: 'h1', fontWeight: 700, color: TEXT.primary }}
        >
            {value ?? 0}
        </Typography>
        <Typography sx={{ typography: 'caption', color: TEXT.muted }}>
            {label}
        </Typography>
    </Paper>
)
Stat.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.number }

/**
 * One trainer notification: everything it holds, where it is in its life,
 * and who has viewed and acknowledged it. A draft can be edited (and
 * published from there) or deleted; a scheduled or active one deactivated.
 */
const TrainerNotificationDetail = () => {
    const { notificationId } = useParams()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [options, setOptions] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [actionError, setActionError] = useState('')
    // 'deactivate' | 'delete' | null: the confirmation open.
    const [confirming, setConfirming] = useState(null)
    const [busy, setBusy] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        setLoadError('')
        try {
            const res = await TrainerNotificationService.get(notificationId)
            setData(res?.data ?? null)
        } catch (err) {
            setLoadError(errorMessage(err?.response))
        } finally {
            setLoading(false)
        }
    }, [notificationId])

    useEffect(() => {
        load()
    }, [load])

    useEffect(() => {
        TrainerNotificationService.audienceOptions()
            .then((res) => setOptions(res?.data ?? null))
            .catch(() => setOptions(null))
    }, [])

    const n = data?.notification
    const report = data?.report
    const lifecycle = n?.lifecycle
    const canDeactivate =
        lifecycle === LIFECYCLE.SCHEDULED || lifecycle === LIFECYCLE.ACTIVE
    const isDraft = lifecycle === LIFECYCLE.DRAFT

    const onConfirm = async () => {
        const action = confirming
        setBusy(true)
        setActionError('')
        try {
            if (action === 'delete') {
                await TrainerNotificationService.remove(notificationId)
                setConfirming(null)
                navigate('/trainer-notifications')
                return
            }
            await TrainerNotificationService.deactivate(notificationId)
            setConfirming(null)
            load()
        } catch (err) {
            setConfirming(null)
            setActionError(
                `${
                    action === 'delete'
                        ? 'The draft was not deleted'
                        : 'It was not deactivated'
                }: ${errorMessage(err?.response)}`
            )
        } finally {
            setBusy(false)
        }
    }

    const trainers = report?.trainers ?? []
    // `available: false` while the report's index is not deployed: its
    // zeros mean "unknown", not "nobody".
    const reportAvailable = report?.available !== false
    const listedIds =
        n?.audience_type === AUDIENCE.TRAINER_IDS
            ? n.audience_trainer_ids ?? []
            : []

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 4 } }}>
                <NotificationHeader
                    title={n?.title || 'Trainer notification'}
                    subtitle="Trainer dashboard notification. Times are IST."
                    actions={
                        <>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    navigate('/trainer-notifications')
                                }
                            >
                                All notifications
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={load}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                            {isDraft && (
                                <>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setConfirming('delete')}
                                    >
                                        Delete draft
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            navigate(
                                                `/trainer-notifications/${encodeURIComponent(
                                                    notificationId
                                                )}/edit`
                                            )
                                        }
                                    >
                                        Edit and publish
                                    </Button>
                                </>
                            )}
                            {canDeactivate && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => setConfirming('deactivate')}
                                >
                                    Deactivate
                                </Button>
                            )}
                        </>
                    }
                />

                {actionError && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={actionError} />
                    </Box>
                )}

                {loading && !n && (
                    <Box
                        role="status"
                        sx={{ display: 'flex', justifyContent: 'center', p: 6 }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                )}

                {loadError && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <ErrorBanner
                            message={`The notification could not be loaded: ${loadError}`}
                        />
                        <Button variant="outlined" onClick={load}>
                            Try again
                        </Button>
                    </Box>
                )}

                {n && (
                    <>
                        <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'minmax(0, 1fr)',
                                        md: 'repeat(4, minmax(0, 1fr))',
                                    },
                                    gap: 2.5,
                                }}
                            >
                                <Field label="Status">
                                    <LifecycleChip lifecycle={lifecycle} />
                                </Field>
                                <Field label="Category">
                                    {CATEGORY_LABEL[n.category] ??
                                        n.category ??
                                        '-'}
                                </Field>
                                <Field label="Tag">
                                    <TagPill tag={n.tag} />
                                </Field>
                                <Field label="Acknowledgement">
                                    {n.ack_required
                                        ? `Required ("${
                                              n.ack_label || 'Acknowledge'
                                          }")`
                                        : 'Not required'}
                                </Field>
                                <Field label="Start">
                                    {whenText(n.start_date)}
                                </Field>
                                <Field label="End">
                                    {whenText(n.end_date)}
                                </Field>
                                <Field label="CTA">
                                    {n.cta_label ? `${n.cta_label} → ` : ''}
                                    {n.cta_link || '-'}
                                    {n.cta_link &&
                                        (n.cta_type === CTA_EXTERNAL
                                            ? ' (external)'
                                            : ' (dashboard page)')}
                                </Field>
                                <Field label="Audience">
                                    {audienceSummary(n, options).join(' · ') ||
                                        '-'}
                                </Field>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <Field label="Message">
                                        {n.message || '-'}
                                    </Field>
                                </Box>
                                {listedIds.length > 0 && (
                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                        <Field
                                            label={`Trainers (${listedIds.length})`}
                                        >
                                            {listedIds
                                                .slice(0, SHOWN_IDS)
                                                .join(', ')}
                                            {listedIds.length > SHOWN_IDS &&
                                                ` and ${
                                                    listedIds.length - SHOWN_IDS
                                                } more`}
                                        </Field>
                                    </Box>
                                )}
                                <Field label="Created">
                                    {n.created_by || '-'}
                                    <br />
                                    {whenText(n.created_on)}
                                </Field>
                                {n.published_on && (
                                    <Field label="Published">
                                        {n.published_by || '-'}
                                        <br />
                                        {whenText(n.published_on)}
                                    </Field>
                                )}
                                {n.updated_on && (
                                    <Field label="Last changed">
                                        {n.updated_by || '-'}
                                        <br />
                                        {whenText(n.updated_on)}
                                    </Field>
                                )}
                                {n.deactivated_on && (
                                    <Field label="Deactivated">
                                        {n.deactivated_by || '-'}
                                        <br />
                                        {whenText(n.deactivated_on)}
                                    </Field>
                                )}
                            </Box>
                            <Box sx={{ mt: 3, maxWidth: 720 }}>
                                <StripPreview
                                    tag={n.tag}
                                    message={n.message}
                                    ctaLabel={n.cta_label}
                                />
                            </Box>
                        </Paper>

                        {!isDraft && !reportAvailable && (
                            <Paper
                                elevation={0}
                                sx={{ ...cardSx, p: 3 }}
                                role="status"
                            >
                                <Typography
                                    sx={{
                                        typography: 'body2',
                                        color: TEXT.secondary,
                                    }}
                                >
                                    Report not available yet. Who viewed and
                                    acknowledged it will show here once the
                                    report is switched on.
                                </Typography>
                            </Paper>
                        )}

                        {!isDraft && reportAvailable && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        mb: 2,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Stat
                                        label="Sent to trainers"
                                        value={report?.recipient_count}
                                    />
                                    <Stat
                                        label="Trainers viewed"
                                        value={report?.viewed_count}
                                    />
                                    <Stat
                                        label="Trainers acknowledged"
                                        value={report?.acknowledged_count}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.muted,
                                        mb: 1,
                                    }}
                                >
                                    Trainers who have at least viewed it.
                                </Typography>
                                <Paper
                                    elevation={0}
                                    sx={{ ...cardSx, overflow: 'hidden' }}
                                >
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {[
                                                        'Trainer',
                                                        'Viewed',
                                                        'Acknowledged',
                                                    ].map((h) => (
                                                        <TableCell
                                                            key={h}
                                                            sx={cellSx}
                                                        >
                                                            <Typography variant="overline">
                                                                {h}
                                                            </Typography>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {trainers.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={3}
                                                            sx={cellSx}
                                                            align="center"
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    typography:
                                                                        'body2',
                                                                    color: TEXT.muted,
                                                                    py: 2,
                                                                }}
                                                            >
                                                                No trainer has
                                                                viewed it yet.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    trainers.map((t) => (
                                                        <TableRow
                                                            key={t.trainer_id}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    ...cellSx,
                                                                    color: TEXT.primary,
                                                                }}
                                                            >
                                                                {t.trainer_id}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    ...cellSx,
                                                                    color: TEXT.secondary,
                                                                }}
                                                            >
                                                                {whenText(
                                                                    t.viewed_on
                                                                )}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    ...cellSx,
                                                                    color: TEXT.secondary,
                                                                }}
                                                            >
                                                                {t.acknowledged_on
                                                                    ? whenText(
                                                                          t.acknowledged_on
                                                                      )
                                                                    : 'Not yet'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </>
                        )}
                    </>
                )}
            </Box>

            <ConfirmDialog
                open={Boolean(confirming)}
                title={
                    confirming === 'delete'
                        ? 'Delete this draft?'
                        : 'Deactivate this notification?'
                }
                message={
                    confirming === 'delete'
                        ? 'The draft is removed for good.'
                        : 'It ends now and leaves every trainer dashboard. This cannot be undone.'
                }
                confirmLabel={
                    busy
                        ? 'Saving...'
                        : confirming === 'delete'
                        ? 'Delete draft'
                        : 'Deactivate'
                }
                destructive
                onConfirm={busy ? () => {} : onConfirm}
                onCancel={() => setConfirming(null)}
            />
        </Box>
    )
}

export default TrainerNotificationDetail
